/**
 * CalibreActionToolbar — shared row of pill buttons (AI Identify, Add as AKA,
 * Create New, optional Push to DB) that operate on a single calibre value.
 * Handles its own AI/AKA modal state; saves go through saveCustomCalibre.
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  doc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
} from 'firebase/firestore';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { Modal } from '../common/Modal';
import {
  db,
  saveCustomCalibre,
  enrichCalibreWithAI,
  getCalibreFamilyById,
  type CustomCalibreEntry,
  type CalibreAliasDetail,
} from '../../services/firebase';
import {
  acceptedCalibreBrandId,
  brandScopeManufacturerIds,
  calibreTiedToBrandViaManufacturers,
} from '../../services/calibreClassification';
import { tiersForBrand } from './calibreTiers';
import { useCalibreSearch } from '../../hooks/useCalibreSearch';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import {
  duplicateAliasMessage,
  findDuplicateAlias,
} from '../../services/calibreResolution';
import {
  AiCalibreEnrichPanel,
  aiSpecsToCalibreData,
  proposedTiersToCalibreTiers,
  resolveBrandManufacturerId,
  resultToDraft,
  type AiEnrichState,
} from './AiCalibreEnrichPanel';
import toast from 'react-hot-toast';

const CUSTOM_CALIBRES_COLLECTION = 'custom_calibres';

// Calibre manufacturers whose movements should NOT be brand-scoped when saved.
export const GENERIC_MOVEMENT_MAKERS = [
  'eta', 'sellita', 'miyota', 'seiko', 'ronda', 'soprod', 'seagull',
  'hangzhou', 'tianjin', 'shanghaid', 'peseux', 'as', 'felsa', 'fontainemelon',
  'valjoux', 'lemania', 'frederic piguet', 'jaeger', 'la joux-perret',
  'kenissi', 'tudor', 'citizen', 'hattori', 'epson', 'casio', 'unitas',
];

export function isGenericMovementName(name: string, manufacturer?: string): boolean {
  const nameLower = name.toLowerCase();
  const manufacturerLower = (manufacturer || '').toLowerCase();
  return GENERIC_MOVEMENT_MAKERS.some(
    (m) => manufacturerLower.includes(m) || nameLower.startsWith(m),
  );
}

export interface CalibreActionToolbarProps {
  calibreName: string;
  brandName: string;
  brandId?: string;
  /**
   * Initial AKA scope. 'brand' means new aliases attach to the contextual
   * brandId by default; the user can override with "Make universal" in the
   * AKA modal. Defaults to 'brand' when brandId/brandName is present, 'all'
   * otherwise.
   */
  defaultAkaScope?: 'brand' | 'all';
  onCalibreCreated?: (docId: string) => void;
  onAliasAdded?: (calibreId: string) => void;
  onPushToDb?: () => void;
  showPushToDb?: boolean;
  showCreateNew?: boolean;
  showAIIdentify?: boolean;
  /**
   * When set, "Add as AKA" skips the calibre picker and attaches the current
   * string directly to this (already-known) calibre — used from the ref editor
   * when the ref is linked, so we don't ask the user to re-pick the target.
   * Scope follows the same default as the modal path (brand-scoped when a
   * brandId is present).
   */
  directAkaTarget?: CustomCalibreEntry;
  compact?: boolean;
  disabled?: boolean;
}

export function CalibreActionToolbar({
  calibreName,
  brandName,
  brandId,
  defaultAkaScope,
  onCalibreCreated,
  onAliasAdded,
  onPushToDb,
  showPushToDb = false,
  showCreateNew = true,
  showAIIdentify = true,
  directAkaTarget,
  compact = false,
  disabled = false,
}: CalibreActionToolbarProps) {
  const trimmed = calibreName.trim();
  const baseDisabled = disabled || !trimmed;

  // AI Identify state. `aiState` holds the async enrichment flow (loading /
  // result / editable draft / error / saving); `refInfo` is the reference-info
  // free text, kept separate so it survives re-runs.
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiState, setAiState] = useState<AiEnrichState>({});
  const [refInfo, setRefInfo] = useState('');
  /**
   * Family tier vocabulary for `directAkaTarget`, when there is one.
   *
   * This toolbar usually acts on a calibre STRING that resolved to nothing —
   * there is no document to read tiers off, and both vocabulary lists are
   * correctly empty. `directAkaTarget` is the exception: the ref editor passes
   * the calibre a linked ref already points at, and re-identifying THAT is
   * exactly when the established tier names should steer the answer.
   */
  const [directTargetFamilyVocab, setDirectTargetFamilyVocab] = useState<string[]>([]);

  useEffect(() => {
    const familyId = directAkaTarget?.familyId?.trim();
    if (!showAIModal || !familyId) {
      setDirectTargetFamilyVocab([]);
      return;
    }
    let cancelled = false;
    getCalibreFamilyById(familyId)
      .then((fam) => {
        if (!cancelled) setDirectTargetFamilyVocab(fam?.tierNames ?? []);
      })
      .catch((err) => {
        console.warn('[CalibreActionToolbar] family tier vocab load failed', err);
        if (!cancelled) setDirectTargetFamilyVocab([]);
      });
    return () => {
      cancelled = true;
    };
  }, [showAIModal, directAkaTarget?.familyId]);

  // AKA state
  const hasBrandContext = brandName.trim().length > 0 || !!brandId;
  const initialAkaScope: 'brand' | 'all' =
    defaultAkaScope ?? (hasBrandContext ? 'brand' : 'all');
  const [showAkaModal, setShowAkaModal] = useState(false);
  // Manufacturer list, loaded when the picker opens, so the brand-scoped AKA
  // picker can also surface calibres tied to the brand through a co-developer /
  // in-house manufacturer (e.g. OP I under Panerai via Officine Panerai) — both
  // as an Algolia filter and as the local tie check on the hydrated documents.
  // Through the shared hook, keyed on the modal being open: same once-per-open
  // cost as the hand-rolled load it replaces, but it also picks up
  // manufacturers created elsewhere in the app, which the old
  // `length > 0` guard would have kept it blind to for the life of the page.
  const { manufacturers: akaManufacturers } = useManufacturerCatalogue({
    enabled: showAkaModal,
  });
  const [akaScope, setAkaScope] = useState<'brand' | 'all'>(initialAkaScope);
  const [akaSearch, setAkaSearch] = useState('');
  const [akaSaving, setAkaSaving] = useState(false);
  // "Make universal" checkbox in the AKA add form. Unchecked → alias is
  // brand-scoped (default when launched from an audit row in a brand context).
  // Checked → alias applies to all brands (omit brandId on the alias detail).
  const [akaMakeUniversal, setAkaMakeUniversal] = useState(false);

  // Create New state
  const [creating, setCreating] = useState(false);

  // Open the modal WITHOUT auto-running — the user adds reference info first,
  // then presses "Run enrichment" inside the panel.
  const handleAIIdentify = useCallback(() => {
    if (!trimmed) return;
    setAiState({});
    setShowAIModal(true);
  }, [trimmed]);

  /** Fetch AI enrichment. Always forwards the preserved reference info;
   *  `existingData` carries the user's corrections on a re-run (ground truth). */
  const runAiEnrich = useCallback(
    async (opts?: { existingData?: Record<string, unknown> }) => {
      if (!trimmed) return;
      setAiState({ loading: true });
      try {
        const result = await enrichCalibreWithAI(trimmed, {
          brandName,
          referenceInfo: refInfo.trim() || undefined,
          existingData: opts?.existingData,
          // General tier names only: calibre enrichment never considers brand
          // tiers (James, 2026-10-29) — a Sellita base carries hundreds of
          // brands' modifications. No brand ⇒ tiersForBrand keeps the general ones.
          existingTierNames: tiersForBrand(directAkaTarget?.data?.tiers, undefined).map((t) => t.name),
          familyTierVocab: directTargetFamilyVocab,
          // Only a resolved calibre has a manufacturer and family to read
          // structured tier names from; an unmatched string sends neither.
          manufacturerId: directAkaTarget?.manufacturerId || undefined,
          familyId: directAkaTarget?.familyId || undefined,
          brandId: brandId || undefined,
        });
        setAiState({ result, draft: resultToDraft(result) });
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'AI lookup failed';
        setAiState({ error: msg });
      }
    },
    [trimmed, brandName, brandId, refInfo, directAkaTarget, directTargetFamilyVocab],
  );

  // Re-run, feeding the user's current (edited) field values back as ground truth.
  const handleRerunWithCorrections = useCallback(() => {
    const draft = aiState.draft;
    const existingData: Record<string, unknown> = { ...(draft?.specs || {}) };
    if (draft?.canonicalName?.trim()) existingData.canonicalName = draft.canonicalName.trim();
    void runAiEnrich({ existingData });
  }, [aiState, runAiEnrich]);

  const handleAcceptAI = useCallback(async () => {
    const { draft, result } = aiState;
    if (!draft || !result) return;
    // Save the user's edited draft (falls back to the raw result fields).
    const canonicalName = draft.canonicalName.trim() || result.canonicalName || trimmed;
    const specs = draft.specs;
    const description = draft.description.trim() || undefined;
    const data = aiSpecsToCalibreData(specs);
    // Persist AI-proposed tier variants (dropped before this fix): the panel
    // doesn't surface them for review, so accept them wholesale — they're
    // already server-sanitised + base-name-stripped. Mirrors the manual
    // editor's data.tiers merge.
    const proposedTiers = proposedTiersToCalibreTiers(draft.tiers);
    if (proposedTiers.length > 0) data.tiers = proposedTiers;
    const isRename = canonicalName.toLowerCase() !== trimmed.toLowerCase();
    const manufacturer = typeof specs.manufacturer === 'string' ? specs.manufacturer.trim() : undefined;
    const isGeneric = isGenericMovementName(canonicalName, manufacturer);
    // The contextual brandId is the ONLY source — the AI's proposed slug is
    // ignored outright (see acceptedCalibreBrandId for why a guessed slug is
    // worse than none). Generic movements stay unscoped.
    const resolvedBrandId = acceptedCalibreBrandId({
      contextBrandId: brandId,
      isGeneric,
    });
    // Owning manufacturer: only the brand when the calibre is in-house (see
    // resolveBrandManufacturerId). The AI's free-text `manufacturer` is a display
    // name, NOT a movement_manufacturers doc id, so it must never be written to
    // manufacturerId directly. manufacturerId is the FK watchlock derives
    // manufacturerIdAncestors from, which the RN explore Manufacturer screen
    // filters on.
    const resolvedManufacturerId = resolveBrandManufacturerId({
      specs,
      brandName,
      brandId: resolvedBrandId,
      isGeneric,
    });

    // On a rename of a brand-scoped calibre, brand-scope the demoted old name so a
    // short/ambiguous alias (e.g. "630") only matches refs of this brand and doesn't
    // collide across brands. Generic movements have no resolvedBrandId → stay universal.
    const demotedAliasDetails: CalibreAliasDetail[] | undefined =
      isRename && resolvedBrandId
        ? [{ name: trimmed, brandId: resolvedBrandId, showInExplore: false }]
        : undefined;

    setAiState((s) => ({ ...s, saving: true }));
    try {
      const docId = await saveCustomCalibre(
        canonicalName,
        data,
        isGeneric ? undefined : (brandName || undefined),
        isRename ? [trimmed] : undefined,
        undefined,
        undefined,
        false,
        description,
        undefined,
        undefined,
        resolvedManufacturerId,
        demotedAliasDetails,
        resolvedBrandId,
      );
      setShowAIModal(false);
      setAiState({});
      toast.success(isRename ? `Saved as "${canonicalName}"` : `Saved "${canonicalName}"`);
      onCalibreCreated?.(docId);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Save failed';
      setAiState((s) => ({ ...s, saving: false, error: msg }));
      toast.error(`Failed to save: ${msg}`);
    }
  }, [aiState, trimmed, brandName, brandId, onCalibreCreated]);

  const handleOpenAka = useCallback(() => {
    if (!trimmed) return;
    setShowAkaModal(true);
    setAkaScope(initialAkaScope);
    setAkaMakeUniversal(false);
    // Seed the search with the value being aliased. The target calibre is
    // usually named after it ("2315" under "Miyota Calibre 2315"), so the row
    // the curator wants is on screen before they type; backspacing broadens
    // back to the full brand-scoped listing.
    setAkaSearch(trimmed);
    // Nothing is loaded here any more: the calibre candidates come from Algolia
    // below (hydrated to real documents, so `handleAddAsAka` still writes
    // against a real entry), and the manufacturer catalogue — which
    // `brandScopeManufacturerIds` and `calibreTiedToBrandViaManufacturers` both
    // read, since watchlock #867 indexed `brandIdsManualInclude` — is loaded by
    // the hook above off this same `showAkaModal` flag.
  }, [trimmed, initialAkaScope]);

  const akaManufacturerById = useMemo(
    () => new Map(akaManufacturers.map((m) => [m.id, m])),
    [akaManufacturers],
  );

  // Server-side scope, so an empty search box still shows this brand's calibres
  // rather than a global page. Omitted for "All brands".
  const akaFilters = useMemo(() => {
    if (akaScope !== 'brand' || !brandId) return {};
    return {
      brandId,
      manufacturerIds: brandScopeManufacturerIds(brandId, akaManufacturers),
    };
  }, [akaScope, brandId, akaManufacturers]);

  const {
    results: akaResults,
    loading: akaLoading,
    degraded: akaDegraded,
  } = useCalibreSearch(akaSearch, {
    enabled: showAkaModal,
    filters: akaFilters,
    hitsPerPage: 100,
    // Brand context with no `brandId` (a legacy free-text `brandName` only) has
    // no index filter that can express it, so the scope below has to run over
    // the whole collection or it would under-report.
    exhaustive: akaScope === 'brand' && !brandId,
  });

  const akaCandidates = useMemo(() => {
    if (!showAkaModal) return [];
    const brandLower = brandName.toLowerCase();
    // Lenient brand-scope match: prefer brandId, fall back to brandName. Still
    // run on the hydrated documents even though Algolia already filtered:
    // `allManufacturerIds` is inflated with heritage ancestors, so the server
    // filter is a superset of what counts as a tie — and in degraded (full-scan)
    // mode it is the only scope filter that runs at all.
    const scoped = akaScope === 'brand'
      ? akaResults.filter((c) => {
          if (brandId && c.brandId === brandId) return true;
          if (!!c.brandName && c.brandName.toLowerCase() === brandLower) return true;
          // Co-developer / in-house case: a calibre carrying no brandId of its
          // own still belongs to this brand if any manufacturer in its set
          // (primary or additional) is tied to it — e.g. ETA-made "OP I" shows
          // under Panerai via its Officine Panerai co-developer.
          return (
            !!brandId &&
            calibreTiedToBrandViaManufacturers(c, brandId, akaManufacturerById)
          );
        })
      : akaResults;
    // Ranked order is Algolia's; only the unfiltered browse list is alphabetised.
    const ordered = akaSearch.trim()
      ? scoped
      : [...scoped].sort((a, b) => a.name.localeCompare(b.name));
    return ordered.slice(0, 100);
  }, [showAkaModal, akaResults, akaManufacturerById, akaScope, akaSearch, brandName, brandId]);

  const handleAddAsAka = useCallback(async (target: CustomCalibreEntry) => {
    if (!trimmed) return;
    setAkaSaving(true);
    try {
      const aliasValue = trimmed;
      // Scope: brand-bound by default when we have a brandId, unless the user
      // checked "Make universal". Universal → no brandId on the alias detail.
      const scopeBrand = brandId && !akaMakeUniversal ? brandId : undefined;
      // Same dedup the calibre editor applies — without it this path appended a
      // second copy of an alias the calibre already carried.
      const duplicate = findDuplicateAlias(target, aliasValue, scopeBrand);
      if (duplicate) {
        toast.error(duplicateAliasMessage(duplicate, aliasValue, target.name));
        return;
      }
      const newAliasDetails: CalibreAliasDetail[] = [
        ...(target.aliasDetails || []),
        scopeBrand
          ? ({ name: aliasValue, brandId: scopeBrand, showInExplore: false } satisfies CalibreAliasDetail)
          : ({ name: aliasValue, showInExplore: false } satisfies CalibreAliasDetail),
      ];
      await updateDoc(doc(db, CUSTOM_CALIBRES_COLLECTION, target.id), {
        alsoKnownAs: arrayUnion(aliasValue),
        aliasDetails: newAliasDetails,
        updatedAt: serverTimestamp(),
      });
      setShowAkaModal(false);
      toast.success(`Added "${aliasValue}" as alias of "${target.name}"`);
      onAliasAdded?.(target.id);
    } catch (error) {
      console.error('Failed to add as AKA:', error);
      const msg = error instanceof Error ? error.message : 'Failed to add alias';
      toast.error(msg);
    } finally {
      setAkaSaving(false);
    }
  }, [trimmed, brandId, akaMakeUniversal, onAliasAdded]);

  const handleCreateNew = useCallback(async () => {
    if (!trimmed) return;
    setCreating(true);
    try {
      const isGeneric = isGenericMovementName(trimmed);
      // Stub create has no AI signal to establish in-house ownership, so we don't
      // guess at manufacturerId (left unset for the user to fill in the editor).
      // brandId still scopes the stub to the contextual brand for non-generic.
      const docId = await saveCustomCalibre(
        trimmed,
        {},
        isGeneric ? undefined : (brandName || undefined),
        undefined,
        undefined,
        undefined,
        false,
        undefined,
        undefined,
        true, // forceCreate
        undefined, // manufacturerId — unknown for a stub
        undefined,
        acceptedCalibreBrandId({ contextBrandId: brandId, isGeneric }),
      );
      toast.success(`Created "${trimmed}"${isGeneric ? ' (unscoped)' : ''}`);
      onCalibreCreated?.(docId);
    } catch (error) {
      console.error('Create calibre failed:', error);
      const msg = error instanceof Error ? error.message : 'Failed to create';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  }, [trimmed, brandName, brandId, onCalibreCreated]);

  const pillSize = compact ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';

  const pillClass = (active: boolean, color: 'purple' | 'indigo' | 'green' | 'emerald') => {
    const palette = {
      purple: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
      indigo: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
      green: 'bg-green-100 text-green-700 hover:bg-green-200',
      emerald: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
    };
    return `inline-flex items-center gap-1 rounded-full font-medium transition-colors ${pillSize} ${
      active ? `${palette[color]} cursor-pointer` : 'bg-gray-100 text-gray-400 cursor-not-allowed'
    }`;
  };

  return (
    <div className='inline-flex items-center gap-1.5 flex-wrap'>
      {showAIIdentify && (
        <button
          type='button'
          onClick={handleAIIdentify}
          disabled={baseDisabled}
          title='Identify this calibre with AI'
          className={pillClass(!baseDisabled, 'purple')}
        >
          <span>✨</span>
          AI Identify
        </button>
      )}

      <button
        type='button'
        onClick={
          directAkaTarget
            ? () => void handleAddAsAka(directAkaTarget)
            : handleOpenAka
        }
        disabled={baseDisabled || akaSaving}
        title={
          directAkaTarget
            ? `Add "${trimmed}" as an alias of ${directAkaTarget.name}`
            : 'Add this value as an alias on an existing calibre'
        }
        className={pillClass(!baseDisabled && !akaSaving, 'indigo')}
      >
        <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
          <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.828 10.172a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656-5.656m-1.414-1.414a4 4 0 00-5.656 5.656l3 3a4 4 0 005.656-5.656' />
        </svg>
        {directAkaTarget
          ? akaSaving
            ? 'Adding…'
            : 'Save as AKA'
          : 'Add as AKA'}
      </button>

      {showCreateNew && (
        <button
          type='button'
          onClick={handleCreateNew}
          disabled={baseDisabled || creating}
          title='Create as new calibre (stub, no enrichment)'
          className={pillClass(!baseDisabled && !creating, 'green')}
        >
          {creating ? '…' : 'Create New'}
        </button>
      )}

      {showPushToDb && onPushToDb && (
        <button
          type='button'
          onClick={onPushToDb}
          disabled={baseDisabled}
          title='Push to custom calibre database'
          className={pillClass(!baseDisabled, 'emerald')}
        >
          <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12' />
          </svg>
          Push to DB
        </button>
      )}

      {/* AI Identify Modal */}
      <Modal
        isOpen={showAIModal}
        onClose={() => {
          if (!aiState.saving) {
            setShowAIModal(false);
            setAiState({});
          }
        }}
        title={`AI Identify: ${trimmed}`}
        size='lg'
      >
        <AiCalibreEnrichPanel
          state={aiState}
          referenceInfo={refInfo}
          onReferenceInfoChange={setRefInfo}
          onRun={() => runAiEnrich()}
          onDraftChange={(d) => setAiState((s) => ({ ...s, draft: d }))}
          onAccept={handleAcceptAI}
          onRerun={() => runAiEnrich()}
          onRerunWithCorrections={handleRerunWithCorrections}
          onClose={() => {
            setShowAIModal(false);
            setAiState({});
          }}
          headerExtra={(() => {
            const d = aiState.draft;
            if (!d) return null;
            const manufacturer = typeof d.specs.manufacturer === 'string'
              ? d.specs.manufacturer.trim()
              : undefined;
            const renamed = d.canonicalName.trim().toLowerCase() !== trimmed.toLowerCase();
            const generic = isGenericMovementName(d.canonicalName, manufacturer);
            return (
              <>
                {renamed && <Badge variant='warning'>renamed</Badge>}
                {generic && <Badge variant='default'>generic — unscoped</Badge>}
              </>
            );
          })()}
        />
      </Modal>

      {/* Add as AKA Modal */}
      <Modal
        isOpen={showAkaModal}
        onClose={() => {
          if (!akaSaving) setShowAkaModal(false);
        }}
        title={`Add as Alias: ${trimmed}`}
        size='lg'
      >
        <div className='space-y-3'>
          <div className='text-sm text-gray-700'>
            Pick the calibre to attach <span className='font-mono font-medium'>{trimmed}</span>{' '}
            to as an alias.
          </div>
          <div className='flex items-center gap-2 flex-wrap'>
            {hasBrandContext && (
              <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-xs'>
                <button
                  type='button'
                  onClick={() => setAkaScope('brand')}
                  className={`px-3 py-1.5 ${
                    akaScope === 'brand' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Current brand only
                </button>
                <button
                  type='button'
                  onClick={() => setAkaScope('all')}
                  className={`px-3 py-1.5 border-l border-gray-300 ${
                    akaScope === 'all' ? 'bg-brand-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All brands
                </button>
              </div>
            )}
            <input
              type='text'
              value={akaSearch}
              onChange={(e) => setAkaSearch(e.target.value)}
              placeholder='Search calibres…'
              className='flex-1 min-w-[200px] border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-1 focus:ring-brand-500 focus:border-brand-500'
            />
          </div>
          {brandId && (
            <label className='inline-flex items-center gap-2 text-xs text-gray-700'>
              <input
                type='checkbox'
                checked={akaMakeUniversal}
                onChange={(e) => setAkaMakeUniversal(e.target.checked)}
                className='rounded text-brand-600 focus:ring-brand-500'
              />
              Make universal (applies to all brands — leave unchecked to scope to{' '}
              <span className='font-mono'>{brandName || brandId}</span>)
            </label>
          )}
          {akaDegraded && (
            <div className='text-xs text-amber-700'>
              Search is unavailable — showing an unranked local match over every
              calibre. Spelling has to be exact.
            </div>
          )}
          <div className='max-h-[50vh] overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100'>
            {akaLoading && akaCandidates.length === 0 ? (
              <div className='p-3 text-sm text-gray-500 italic'>Loading…</div>
            ) : akaCandidates.length === 0 ? (
              <div className='p-3 text-sm text-gray-500 italic'>No calibres match.</div>
            ) : (
              akaCandidates.map((c) => (
                <button
                  key={c.id}
                  type='button'
                  onClick={() => handleAddAsAka(c)}
                  disabled={akaSaving}
                  className='w-full text-left p-2 hover:bg-brand-50 disabled:opacity-50 text-sm flex items-baseline gap-2'
                >
                  <span className='font-mono font-medium'>{c.name}</span>
                  {(c.brandId || c.brandName) && (
                    <span className='text-gray-500 text-xs'>
                      ({c.brandId || c.brandName})
                    </span>
                  )}
                  {c.data?.movementType && (
                    <span className='text-gray-400 text-xs'>— {c.data.movementType}</span>
                  )}
                  {c.alsoKnownAs && c.alsoKnownAs.length > 0 && (
                    <span className='text-gray-400 text-xs truncate'>
                      aka: {c.alsoKnownAs.slice(0, 3).join(', ')}
                      {c.alsoKnownAs.length > 3 ? '…' : ''}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
          <div className='flex justify-end pt-2 border-t border-gray-100'>
            <Button
              variant='ghost'
              onClick={() => setShowAkaModal(false)}
              disabled={akaSaving}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
